'use client';
import React, { useState, useRef, useEffect } from 'react';
import sparrowLogo from '../../assets/images/sparrow.png';
import styles from './SparrowChat.module.css';
import axios from 'axios';
import { marked } from 'marked';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';

const apiKey = import.meta.env.VITE_SPARROW_API_KEY;
const sparrowBaseUrl = import.meta.env.VITE_SPARROW_API_BASE_URL;

const SparrowChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([
    { id: 1, title: 'Chat with Sparrow 1', messages: [{ sender: 'assistant', text: 'Welcome back!' }] },
    { id: 2, title: 'PO Inquiry', messages: [{ sender: 'assistant', text: 'What project are you referring to?' }] },
  ]);
  const [activeChatId, setActiveChatId] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);

  const bottomRef = useRef(null);

  useEffect(() => {
    const currentChat = chatHistory.find((chat) => chat.id === activeChatId);
    setMessages(currentChat?.messages || []);
  }, [activeChatId]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { sender: 'user', text: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('message', userMessage.text);
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await axios.post(`${sparrowBaseUrl}/chat`, formData, { headers: { 'x-api-key': apiKey } });

      const botMessage = {
        sender: 'assistant',
        text: response.data.response || 'No response.',
      };

      const updatedMessages = [...newMessages, botMessage];
      setMessages(updatedMessages);
    } catch (err) {
      console.error('Error contacting Sparrow LLM:', err);
      setMessages((prev) => [...prev, { sender: 'assistant', text: 'Error talking to Sparrow AI.' }]);
    } finally {
      setLoading(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // const handleSend = async () => {
  //   if (!input.trim()) return;

  //   const userMessage = { sender: 'user', text: input.trim() };
  //   const newMessages = [...messages, userMessage];
  //   setMessages(newMessages);
  //   setInput('');
  //   setLoading(true);

  //   try {
  //     const response = await fetch('http://127.0.0.1:8000/chat/stream', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'x-api-key': apiKey,
  //       },
  //       body: JSON.stringify({
  //         message: userMessage.text,
  //       }),
  //     });

  //     if (!response.body) throw new Error('No response stream');

  //     const reader = response.body.getReader();
  //     const decoder = new TextDecoder();
  //     let assistantMessage = '';

  //     while (true) {
  //       const { done, value } = await reader.read();
  //       if (done) break;

  //       const chunk = decoder.decode(value, { stream: true });
  //       assistantMessage += chunk;

  //       setMessages((prevMessages) => {
  //         const updated = [...prevMessages];
  //         const last = updated[updated.length - 1];

  //         // Append or create assistant message
  //         if (last && last.sender === 'assistant') {
  //           updated[updated.length - 1] = {
  //             ...last,
  //             text: last.text + chunk,
  //           };
  //         } else {
  //           updated.push({ sender: 'assistant', text: chunk });
  //         }

  //         return updated;
  //       });
  //     }
  //   } catch (err) {
  //     console.error('Error streaming from Sparrow:', err);
  //     setMessages((prev) => [...prev, { sender: 'assistant', text: 'Error talking to Sparrow AI.' }]);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const fileInputRef = useRef(null);

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      console.log('Selected file:', file);
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex' }}>
      {/* History Sidebar */}

      {/* Chat Section */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        {/* Messages */}
        <Paper elevation={3} sx={{ flex: 1, overflowY: 'auto', p: 2, borderRadius: 2, bgcolor: '#f5f5f5' }}>
          {messages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                mb: 1,
              }}
            >
              <Box
                sx={{
                  maxWidth: '75%',
                  bgcolor: msg.sender === 'user' ? '#1976d2' : '#e0e0e0',
                  color: msg.sender === 'user' ? 'white' : 'black',
                  p: 1.5,
                  borderRadius: 2,
                }}
              >
                {msg.sender === 'assistant' ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box component="img" src={sparrowLogo} alt="Sparrow" sx={{ width: 24, height: 24, mt: '4px' }} />
                    <Box
                      component="div"
                      sx={{
                        fontSize: '1rem',
                        textAlign: 'left',
                        lineHeight: 1.6,
                        '& pre': {
                          backgroundColor: '#272822',
                          color: '#f8f8f2',
                          padding: '8px',
                          borderRadius: '4px',
                          overflowX: 'auto',
                        },
                        '& code': {
                          fontFamily: 'monospace',
                          backgroundColor: '#f0f0f0',
                          padding: '2px 4px',
                          borderRadius: '4px',
                        },
                        '& a': {
                          color: '#1976d2',
                          textDecoration: 'underline',
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: marked.parse(msg.text) }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body1">{msg.text}</Typography>
                )}
              </Box>
            </Box>
          ))}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          <div ref={bottomRef} />
        </Paper>

        {/* Input */}
        <Box sx={{ mt: 2 }}>
          {/* Hidden file input */}
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />

          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleFileClick}>
                    <AttachFileIcon />
                  </IconButton>
                  <IconButton onClick={handleSend} disabled={loading || (!input.trim() && !selectedFile)}>
                    <SendIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {selectedFile && (
            <Typography variant="body2" sx={{ mt: 1, ml: 1 }}>
              📎 Attached: {selectedFile.name}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SparrowChat;
